import isTruthy from "@/lib/utils/boolean/isTruthy";
import { TZDate } from "@date-fns/tz";
import { CourtSummary, CrewSummary, RunSummary, Status } from "@shared/lib/database/query.types";
import { format, parseISO } from "date-fns";
import { computed, Ref } from "vue";
import useRunStatus from "./useRunStatus";

// TODO: break up into dates, counts, and states
export default function (run: Ref<RunSummary & {
  statuses: Array<Pick<Status, "probability">>,
  crews: Array<CrewSummary>,
  court: CourtSummary | null,
}>) {
  // TODO: refactor with useRunSchedule
  const startDate = computed(() => parseISO(run.value.start_at));
  const endDate = computed(() => run.value.end_at ? parseISO(run.value.end_at) : null);

  const startDateInTimezone = computed(() => new TZDate(startDate.value, run.value.court?.timezone || "America/New_York"));
  const endDateInTimezone = computed(() => endDate.value ? new TZDate(endDate.value, run.value.court?.timezone || "America/New_York") : null);

  const formattedShortDate = computed(() => format(startDateInTimezone.value, "EEE, MMM d"));
  const formattedLongDate = computed(() => format(startDateInTimezone.value, "EEEE, MMMM d, yyyy"));
  const formattedStartTime = computed(() => formatTime(startDateInTimezone.value));
  const formattedEndTime = computed(() => formatTime(endDateInTimezone.value));

  const formattedTimeRange = computed(() => {
    return [formattedStartTime.value, formattedEndTime.value].filter(isTruthy).join(" – ");
  });

  const inCount = computed(() => run.value.statuses.filter((status) => status.probability === 1).length);
  const maybeCount = computed(() => run.value.statuses.filter((status) => status.probability === 0.5).length);
  const outCount = computed(() => run.value.statuses.filter((status) => status.probability === 0).length);

  const spotCount = computed(() => {
    if (!run.value || run.value.canceled_at) return 0;

    const defaultMinCount = 8;
    return inCount.value < (run.value.min_player_count || 0)
      ? run.value.min_player_count || run.value.max_player_count || defaultMinCount
      : run.value.max_player_count || Math.max(inCount.value, run.value.min_player_count || defaultMinCount);
  });

  const neededPlayerCount = computed(() => {
    return Math.max(0, (run.value.min_player_count || 0) - inCount.value);
  });

  const openSpotCount = computed(() => {
    return Math.max(0, (run.value.max_player_count || 0) - inCount.value);
  });

  const isGameOn = computed(() => {
    return run.value.min_player_count && inCount.value >= run.value.min_player_count;
  });

  // TODO: remove outside references to these, then remove these
  const {
    hasRunStarted,
    hasRunEnded,
    isRunApproaching,
    isRunActive,
    isRunActiveOrApproaching,
  } = useRunStatus(run);

  const canInteractWithRun = computed(() => {
    return !run.value.canceled_at
      && (!hasRunStarted.value || isRunActive.value);
  });

  function formatTime (date: Date | null): string | null {
    if (!date) return null;

    return format(date, date.getMinutes() > 0 ? "h:mmaaa" : "haaa");
  }

  return {
    startDate,
    startDateInTimezone,
    formattedShortDate,
    formattedLongDate,
    formattedStartTime,
    formattedEndTime,
    formattedTimeRange,
    inCount,
    maybeCount,
    outCount,
    spotCount,
    neededPlayerCount,
    openSpotCount,
    isGameOn,
    hasRunStarted,
    hasRunEnded,
    isRunApproaching,
    isRunActive,
    isRunActiveOrApproaching,
    canInteractWithRun,
  };
}
